import React, { useEffect, useRef, useState } from 'react';
import { useEditorStore } from '../store/editorStore';
import * as esbuild from 'esbuild-wasm';
import { Loader2 } from 'lucide-react';

const Preview: React.FC = () => {
  const { project, previewKey, isPreviewLoading, setPreviewLoading, addConsoleMessage, externalLibraries } = useEditorStore();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [error, setError] = useState<string | null>(null);

  // 初始化esbuild
  useEffect(() => {
    const initializeEsbuild = async () => {
      try {
        await esbuild.initialize({
          wasmURL: 'https://unpkg.com/esbuild-wasm@0.19.5/esbuild.wasm',
          worker: true
        });
      } catch (err) {
        console.error('Failed to initialize esbuild:', err);
      }
    };

    initializeEsbuild();
  }, []);

  // 编译TypeScript
  const compileTypeScript = async (code: string): Promise<string> => {
    try {
      const result = await esbuild.transform(code, {
        loader: 'ts',
        format: 'iife',
        target: 'es2020'
      });
      return result.code;
    } catch (err) {
      console.error('TypeScript compilation error:', err);
      throw err;
    }
  };

  // 生成完整的HTML
  const generateHTML = async () => {
    const files = project.files;
    let html = files[project.rootFileId]?.content || '';

    // 处理CSS
    const cssFiles = Object.values(files).filter(f => f.language === 'css');
    let cssContent = cssFiles.map(f => f.content).join('\n');

    // 处理JavaScript/TypeScript
    const jsFiles = Object.values(files).filter(f => f.language === 'javascript' || f.language === 'typescript');
    let jsContent = '';

    for (const file of jsFiles) {
      if (file.language === 'typescript') {
        try {
          const compiled = await compileTypeScript(file.content || '');
          jsContent += compiled + '\n';
        } catch (err) {
          throw new Error(`TypeScript编译错误 in ${file.name}: ${err}`);
        }
      } else {
        jsContent += file.content + '\n';
      }
    }

    // 注入CSS
    html = html.replace('</head>', `<style>${cssContent}</style></head>`);

    // 注入外部库
    if (externalLibraries.length > 0) {
      const externalScripts = externalLibraries
        .map(url => `<script src="${url}"></script>`)
        .join('\n');
      html = html.replace('</head>', `${externalScripts}</head>`);
    }

    // 注入JavaScript和console重写
    const consoleScript = `
      <script>
        (function() {
          const originalConsole = window.console;
          const logs = [];

          function sendToParent(message) {
            window.parent.postMessage({ type: 'console', data: message }, '*');
          }

          window.console = {
            log: function(...args) {
              originalConsole.log.apply(originalConsole, args);
              sendToParent({ type: 'log', content: args.map(arg =>
                typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
              ).join(' ') });
            },
            error: function(...args) {
              originalConsole.error.apply(originalConsole, args);
              sendToParent({ type: 'error', content: args.map(arg =>
                typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
              ).join(' ') });
            },
            warn: function(...args) {
              originalConsole.warn.apply(originalConsole, args);
              sendToParent({ type: 'warn', content: args.map(arg =>
                typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
              ).join(' ') });
            },
            info: function(...args) {
              originalConsole.info.apply(originalConsole, args);
              sendToParent({ type: 'info', content: args.map(arg =>
                typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
              ).join(' ') });
            }
          };

          window.addEventListener('error', function(e) {
            sendToParent({ type: 'error', content: e.message });
          });
        })();
      </script>
    `;

    html = html.replace('</body>', `${consoleScript}<script>${jsContent}</script></body>`);

    return html;
  };

  // 更新预览
  useEffect(() => {
    const updatePreview = async () => {
      if (!iframeRef.current) return;

      setError(null);
      setPreviewLoading(true);

      try {
        const html = await generateHTML();
        const iframe = iframeRef.current;

        iframe.srcdoc = html;
      } catch (err) {
        setError(err instanceof Error ? err.message : '预览生成失败');
        addConsoleMessage({
          type: 'error',
          content: `预览错误: ${err instanceof Error ? err.message : '未知错误'}`
        });
      } finally {
        setPreviewLoading(false);
      }
    };

    updatePreview();
  }, [previewKey, project, externalLibraries]);

  // 监听iframe消息
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'console') {
        addConsoleMessage({
          type: event.data.data.type,
          content: event.data.data.content
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <div className="h-full bg-white relative">
      {error && (
        <div className="absolute top-0 left-0 right-0 bg-red-500 text-white p-2 text-sm z-10">
          {error}
        </div>
      )}

      {isPreviewLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-editor-bg bg-opacity-80 z-20">
          <div className="flex items-center gap-2 text-gray-300">
            <Loader2 className="animate-spin" size={20} />
            <span>正在加载...</span>
          </div>
        </div>
      )}

      <iframe
        ref={iframeRef}
        className="w-full h-full border-0"
        sandbox="allow-scripts allow-modals allow-forms"
        title="preview"
      />
    </div>
  );
};

export default Preview;
